/*
 © Copyright 2026 Daikin Industries, Ltd. All rights reserved.

 This software is the proprietary information of Daikin Industries, Ltd.
 By using this software, you agree to the following terms:

 1. You are granted a revocable non-exclusive, non-transferable license to use this software.
 2. You agree not to modify, distribute, or create derivative works based on this software.
 3. Unauthorized copying, distribution, or use of any portion of this software is strictly prohibited.
 4. This software is protected by copyright laws and international treaties.
 5. Any violation of these terms will result in the termination of your license and may result in legal action.
 6. This is Daikin's proprietary software and may not be used in an unauthorized manner or without express authorization from Daikin.
*/

import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ApiDiscoverabilityService, ApiOperation } from '../../services/api-discoverability.service';
import { ApiMeshStateService } from '../../services/api-mesh-state.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-api-mesh-explorer',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './api-mesh-explorer.component.html',
  styleUrl: './api-mesh-explorer.component.scss'
})
export class ApiMeshExplorerComponent implements OnInit, OnDestroy {
  apiOperations: ApiOperation[] = [];
  filteredOperations: ApiOperation[] = [];
  loading = false;
  error: string | null = null;
  searchTerm = '';
  private stateSubscription?: Subscription;

  constructor(
    private apiDiscoverabilityService: ApiDiscoverabilityService,
    private apiMeshStateService: ApiMeshStateService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.loadApiOperations();

    // Subscribe to state changes to restore search state when coming back from dynamic viewer
    this.stateSubscription = this.apiMeshStateService.state$.subscribe(state => {
      if (!state.showDynamicViewer && state.explorerState) {
        this.searchTerm = state.explorerState.searchTerm || '';
        if (state.explorerState.filteredOperations) {
          this.filteredOperations = state.explorerState.filteredOperations;
        }
      }
    });
  }

  loadApiOperations(): void {
    this.loading = true;
    this.error = null;

    this.apiDiscoverabilityService.getApiOperations().subscribe({
      next: (response) => {
        this.apiOperations = response.data;

        this.apiOperations.forEach(operation => {
          operation.displayName = operation.name;
        });

        // Check if we have saved state to restore
        const currentState = this.apiMeshStateService.getCurrentState();
        if (currentState.explorerState && currentState.explorerState.searchTerm) {
          this.searchTerm = currentState.explorerState.searchTerm;
          this.onSearch();
        } else {
          this.filteredOperations = [...this.apiOperations];
        }

        this.loading = false;
        console.log('Loaded API operations:', this.apiOperations.length);
      },
      error: (error) => {
        this.error = error.message || 'Failed to load API operations';
        this.loading = false;
        console.error('Error loading API operations:', error);
      }
    });
  }

  onSearch(): void {
    if (!this.searchTerm.trim()) {
      this.filteredOperations = [...this.apiOperations];
      return;
    }

    const searchTerm = this.searchTerm.toLowerCase().trim();
    this.filteredOperations = this.apiOperations.filter(operation =>
      operation.name.toLowerCase().includes(searchTerm) ||
      (operation.displayName && operation.displayName.toLowerCase().includes(searchTerm)) ||
      operation.description.toLowerCase().includes(searchTerm) ||
      operation.applicationName.toLowerCase().includes(searchTerm) ||
      operation.method.toLowerCase().includes(searchTerm) ||
      operation.path.toLowerCase().includes(searchTerm)
    );

    // Update state service with current explorer state
    this.updateExplorerState();
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.filteredOperations = [...this.apiOperations];
    this.updateExplorerState();
  }

  sendRequest(operation: ApiOperation): void {
    console.log('Opening dynamic Swagger UI for operation:', operation);

    // Save current explorer state before opening dynamic viewer
    const explorerState = {
      searchTerm: this.searchTerm,
      filteredOperations: [...this.filteredOperations]
    };

    // Open dynamic viewer within the same component using state service
    this.apiMeshStateService.openDynamicViewer(operation, explorerState);
  }

  getMethodClass(method: string): string {
    switch (method.toLowerCase()) {
      case 'get': return 'method-get';
      case 'post': return 'method-post';
      case 'put': return 'method-put';
      case 'delete': return 'method-delete';
      case 'patch': return 'method-patch';
      default: return 'method-default';
    }
  }

  /**
   * Format operation name by adding spaces before capital letters
   * Example: 'GetDevice' becomes 'Get Device'
   * Example: 'GDBTestCase' becomes 'GDB Test Case'
   * Example: 'NameGBC' becomes 'Name GBC'
   */
  formatOperationName(name: string): string {
    if (!name) return '';
    return name
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .trim();
  }

  retry(): void {
    this.loadApiOperations();
  }

  private updateExplorerState(): void {
    const explorerState = {
      searchTerm: this.searchTerm,
      filteredOperations: [...this.filteredOperations]
    };
    this.apiMeshStateService.updateExplorerState(explorerState);
  }

  ngOnDestroy(): void {
    if (this.stateSubscription) {
      this.stateSubscription.unsubscribe();
    }
  }
}
